import { useEffect, useState } from "react";
import axios from "axios";
import { toolRight } from "./access";

const API_BASE = process.env.REACT_APP_API_URL || "";

// Legacy logins with full read access but no changes on the Reorder screens (2026-10-12: the PIN-era "Approver" login is gone;
// the PIN-era BUYER_ACCESS / getBuyerAccess map was replaced by per-person departments in Users & Access).
export const READONLY_BUYERS = ["Ecom"];

export const DEPT_TO_BUYER = {
  Petticoat: "Saleem",
  Blouse:    "Saleem",
  Saree:     "Nanik",
  Unstitch:  "Salman",
  Dupatta:   "DVC",
};

export const BUYERS = ["All", "Saleem", "Nanik", "Salman", "DVC", "Shailendra"];

// Colours resolve through CSS variables (App.css "Buyer colours") so they adapt to dark mode; hex = light fallback.
export const BUYER_COLORS = {
  Saleem:     { bg: "var(--bc-saleem-bg, #fce7f3)", text: "var(--bc-saleem-text, #9d174d)" },
  Nanik:      { bg: "var(--bc-nanik-bg, #dbeafe)", text: "var(--bc-nanik-text, #1e40af)" },
  Salman:     { bg: "var(--bc-salman-bg, #dcfce7)", text: "var(--bc-salman-text, #166534)" },
  DVC:        { bg: "var(--bc-dvc-bg, #fef9c3)", text: "var(--bc-dvc-text, #854d0e)" },
  Shailendra: { bg: "var(--bc-shailendra-bg, #f3e8ff)", text: "var(--bc-shailendra-text, #6b21a8)" },
};

// 2026-10-08 access v2: dynamic department -> label store (edit buyers). Until loadDepartmentMap() succeeds
// (PIN mode / fetch failure) every function below behaves exactly as before.
const LEGACY_BUYERS = ["All", "Saleem", "Nanik", "Salman", "DVC", "Shailendra"];
const LEGACY_COLOR_NAMES = new Set(Object.keys(BUYER_COLORS));
const PALETTE = [
  { bg: "var(--bc-p0-bg, #ffedd5)", text: "var(--bc-p0-text, #9a3412)" },
  { bg: "var(--bc-p1-bg, #cffafe)", text: "var(--bc-p1-text, #155e75)" },
  { bg: "var(--bc-p2-bg, #fee2e2)", text: "var(--bc-p2-text, #991b1b)" },
  { bg: "var(--bc-p3-bg, #e0e7ff)", text: "var(--bc-p3-text, #3730a3)" },
  { bg: "var(--bc-p4-bg, #d1fae5)", text: "var(--bc-p4-text, #065f46)" },
  { bg: "var(--bc-p5-bg, #fae8ff)", text: "var(--bc-p5-text, #86198f)" },
  { bg: "var(--bc-p6-bg, #ecfccb)", text: "var(--bc-p6-text, #3f6212)" },
  { bg: "var(--bc-p7-bg, #e2e8f0)", text: "var(--bc-p7-text, #334155)" },
];
const UNASSIGNED = "Unassigned";

let deptMap = null;          // { [trimmed dept]: { label } } once the department overview loaded (accounts mode)
let overviewFailed = false;  // overview endpoint unreachable -> fall back to the built-in legacy mapping for labels
let currentUser = null;
const listeners = new Set();
let version = 0;

const hashIdx = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % PALETTE.length;
};

const notify = () => { version += 1; listeners.forEach((cb) => { try { cb(version); } catch (e) { console.error("department listener failed", e); } }); };

export const subscribeDepartments = (cb) => { listeners.add(cb); return () => listeners.delete(cb); };

export const useDepartmentMap = () => {
  const [v, setV] = useState(version);
  useEffect(() => { setV(version); return subscribeDepartments(setV); }, []);
  return v;
};

const normDept = (d) => (d == null ? "" : String(d).trim());
const nameOf = (x) => (x && typeof x === "object" ? (x.name || x.buyer_name || "") : String(x || ""));

// Access v2: department label = names of buyers with EDIT on it (server computes; joined "A, B"), else "Unassigned".
const labelFor = (dept) => {
  const e = deptMap && deptMap[normDept(dept)];
  if (e && e.label) return e.label;
  return overviewFailed ? (DEPT_TO_BUYER[normDept(dept)] || UNASSIGNED) : UNASSIGNED;
};

const rebuildBuyers = () => {
  const names = [];
  Object.values(deptMap || {}).forEach((e) => { const n = e.label || UNASSIGNED; if (n && !names.includes(n)) names.push(n); });
  names.sort((a, b) => {
    const ia = LEGACY_BUYERS.indexOf(a), ib = LEGACY_BUYERS.indexOf(b);
    if (ia >= 0 && ib >= 0) return ia - ib;
    if (ia >= 0) return -1;
    if (ib >= 0) return 1;
    return a.localeCompare(b);
  });
  BUYERS.length = 0;
  BUYERS.push("All", ...names);
  names.forEach((n) => {
    if (LEGACY_COLOR_NAMES.has(n)) return;
    BUYER_COLORS[n] = PALETTE[hashIdx(n)];
  });
};

// Accepts the department overview in any of: array, {items}, {departments}; each row may carry
// label | buyer_name | edit_buyers[] (names or {name}). Falls back to the signed-in user's own list.
const parseOverview = (data) => {
  const rows = Array.isArray(data) ? data : ((data && (data.items || data.departments)) || []);
  const m = {};
  rows.forEach((it) => {
    const name = normDept(it && it.name);
    if (!name) return;
    let label = it.label || it.buyer_name || "";
    if (!label && Array.isArray(it.edit_buyers)) label = it.edit_buyers.map(nameOf).filter(Boolean).join(", ");
    m[name] = { label: label || UNASSIGNED };
  });
  return m;
};

export const loadDepartmentMap = async () => {
  let m = {};
  overviewFailed = false;
  try {
    const res = await axios.get(`${API_BASE}/api/account/departments`, { withCredentials: true });
    m = parseOverview(res.data);
  } catch (e) {
    overviewFailed = true;
    console.warn("Department overview not available; labels fall back to built-in mapping", e);
  }
  // make sure the user's own departments always exist (label: legacy mapping if the overview did not list them)
  ((currentUser && currentUser.departments) || []).forEach((d) => {
    const n = normDept(d && d.name);
    if (n && !m[n]) m[n] = { label: DEPT_TO_BUYER[n] || UNASSIGNED };
  });
  deptMap = m;
  rebuildBuyers();
  notify();
};

export const setCurrentUser = (user) => { currentUser = user || null; notify(); };

// true once the signed-in user is loaded (App.js AccountsBridge). 2026-10-12: PIN mode is gone; "false" now only means
// "not loaded yet" (or a test render without a user) - the checks below then stay permissive and the server still enforces.
export const isAccountsMode = () => !!currentUser;

// 2026-10-13 Customise access: the server's dept_access.all decides (admin / all-departments buyer / a non-buyer WITHOUT departments);
// an Ecom / WH / CRM person given departments sees only those. Fallback (older server answer): the pre-2026-10-13 team rule.
const seesAll = () => {
  if (!currentUser) return false;
  if (currentUser.dept_access && typeof currentUser.dept_access.all === "boolean") return currentUser.dept_access.all;
  return currentUser.team === "admin" || currentUser.team === "ecom" || !!currentUser.all_departments;
};
// Non-buyer limited to departments (Customise access): same rule as backend access.deptScoped.
const deptScoped = () => !!currentUser && currentUser.team !== "admin" && currentUser.team !== "buyer" && ((currentUser.departments || []).length > 0);

const deptEntry = (dept) => {
  const d = normDept(dept);
  return ((currentUser && currentUser.departments) || []).find((x) => normDept(x && x.name) === d) || null;
};

export const canSeeDepartment = (dept) => {
  if (!isAccountsMode()) return true; // user not loaded yet (see isAccountsMode)
  if (seesAll()) return true;
  return !!deptEntry(dept);
};

// Edit rights on a department's data (mirror of backend access.canEditDept): admin; otherwise Edit on the Reorder tool AND
// buyer: all_departments or that department on Edit / non-buyer: no departments set (all) or that department on Edit.
export const canEditDepartment = (dept) => {
  if (!isAccountsMode()) return true; // user not loaded yet (see isAccountsMode); the server enforces edit rights
  if (currentUser.team === "admin") return true;
  if (toolRight(currentUser, "reorder") !== "edit") return false;
  if (currentUser.team === "buyer" && currentUser.all_departments) return true;
  if (currentUser.team !== "buyer" && !deptScoped()) return true;
  const e = deptEntry(dept);
  return !!(e && e.access === "edit");
};

// Screens pass their allowedBuyers prop through this: null (sees all) or the labels of the departments the viewer can see
// (user not loaded yet: the prop is returned unchanged).
export const resolveAllowedBuyers = (allowedBuyersProp) => {
  if (!isAccountsMode()) return allowedBuyersProp;
  if (seesAll()) return null;
  const names = new Set();
  ((currentUser && currentUser.departments) || []).forEach((d) => { names.add(labelFor(d && d.name)); });
  if (!names.size && currentUser.name) names.add(currentUser.name);
  return Array.from(names);
};

// Row-level access check used by list screens (user not loaded yet: owner name in allowedBuyers).
export const deptAllowed = (dept, allowedBuyers) => {
  if (isAccountsMode()) return canSeeDepartment(dept);
  return !allowedBuyers || allowedBuyers.includes(getBuyer(dept));
};

// Stable display order for owner names: legacy order first, then alphabetical.
export const sortBuyerNames = (names) => names.slice().sort((a, b) => {
  const ia = LEGACY_BUYERS.indexOf(a), ib = LEGACY_BUYERS.indexOf(b);
  if (ia >= 0 && ib >= 0) return ia - ib;
  if (ia >= 0) return -1;
  if (ib >= 0) return 1;
  return a.localeCompare(b);
});

export const getBuyer = (dept) => {
  if (deptMap) return labelFor(dept);
  return DEPT_TO_BUYER[dept?.trim()] || "Shailendra";
};

// Format a date string or Date object as "04 Mar 2026"
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
export const fmtDate = (d) => {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d.includes("T") ? d : d + "T00:00:00") : d;
  if (isNaN(dt)) return d;
  return `${String(dt.getDate()).padStart(2,"0")} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
};

export const computeStats = (items) => {
  // non_ecom_po counts as "created" — it has a PO, just not an ecom one
  const po_created = items.filter((i) => i.po_created || i.non_ecom_po).length;
  const days = items.filter((i) => i.days_to_po !== null).map((i) => i.days_to_po);
  const avg = days.length ? +(days.reduce((a, b) => a + b, 0) / days.length).toFixed(1) : null;
  const total_req_qty    = items.reduce((s, i) => s + (i.total_requested_qty || 0), 0);
  const po_created_qty   = items.filter((i) => i.po_created || i.non_ecom_po).reduce((s, i) => s + (i.total_po_qty || 0), 0);
  const grn_qty          = items.reduce((s, i) => s + (i.grn_qty || 0), 0);
  const grn_done         = items.filter((i) => (i.grn_qty || 0) > 0).length;
  const grn_full         = items.filter((i) => (i.total_po_qty || 0) > 0 && (i.grn_qty || 0) >= (i.total_po_qty || 0)).length;
  return {
    total_styles: items.length,
    po_created,
    po_pending: items.length - po_created,
    avg_days_to_po: avg,
    pct_created: items.length ? +(po_created / items.length * 100).toFixed(1) : 0,
    total_req_qty,
    po_created_qty,
    grn_qty,
    grn_done,
    grn_full,
  };
};
